// Web Mercator framing for a route over map tiles: fit the route into a box, list the tiles that
// cover the view, and project points into the same space so the line sits exactly on the map.
// Tiles are CARTO's basemaps (OpenStreetMap data), at @2x. Attribution: © OpenStreetMap © CARTO.
export type P = [number, number]; // [lat, lon]
export const ATTRIBUTION = "© OpenStreetMap © CARTO";
const T = 256;
const wx = (lon: number) => ((lon + 180) / 360) * T;
const wy = (lat: number) => { const s = Math.sin((lat * Math.PI) / 180); return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * T; };

export type Tile = { z: number; x: number; y: number; px: number; py: number; size: number };
export type Frame = { at: (p: P) => [number, number]; tiles: Tile[]; z: number };

/** Fit pts into box (within a view of vw by vh units). bias picks the tile zoom: 0 for CSS pixels
 *  on a retina screen, -1 for device pixels such as a 1080 px canvas. */
export function frame(pts: P[], vw: number, vh: number, box = { x: 0, y: 0, w: vw, h: vh }, bias = 0, maxZ = 16): Frame {
  const xs = pts.map((p) => wx(p[1])), ys = pts.map((p) => wy(p[0]));
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  const k = Math.min(box.w / Math.max(1e-9, x1 - x0), box.h / Math.max(1e-9, y1 - y0), T * 2 ** maxZ / T);
  const ox = box.x + (box.w - (x1 - x0) * k) / 2 - x0 * k, oy = box.y + (box.h - (y1 - y0) * k) / 2 - y0 * k;
  const z = Math.max(1, Math.min(maxZ, Math.floor(Math.log2(k) + bias)));
  const n = 2 ** z, size = (T / n) * k;
  const tx0 = Math.floor(((0 - ox) / k) * n / T), tx1 = Math.floor(((vw - ox) / k) * n / T);
  const ty0 = Math.max(0, Math.floor(((0 - oy) / k) * n / T)), ty1 = Math.min(n - 1, Math.floor(((vh - oy) / k) * n / T));
  const tiles: Tile[] = [];
  for (let y = ty0; y <= ty1; y++) for (let x = tx0; x <= tx1; x++) tiles.push({ z, x: ((x % n) + n) % n, y, px: ox + x * size, py: oy + y * size, size });
  return { at: (p) => [ox + wx(p[1]) * k, oy + wy(p[0]) * k], tiles, z };
}

// CARTO's basemaps need a key since autumn 2026. The key stays on the server: tiles go through
// Musette's /api/img, which adds it. Without a key the proxy refuses and the map is the route alone.
export const tileUrl = (t: Tile, dark: boolean) => `https://basemaps.cartocdn.com/rastertiles/${dark ? "dark_all" : "light_all"}/${t.z}/${t.x}/${t.y}@2x.png`;
/** The same tile through Musette's own origin, so a canvas can export it. */
export const proxied = (u: string) => `/api/img?u=${encodeURIComponent(u)}`;
